import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import PublicLayout, { PrimaryBtn, GhostBtn } from "@/components/public/PublicLayout";
import { ROUTES, C, SERIF } from "@/lib/publicLinks";
import { useCart } from "@/context/CartContext";

export default function ShopOrderReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const { clear } = useCart();
  const [state, setState] = useState("verifying"); // verifying | paid | unpaid | error
  const [order, setOrder] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    if (!orderId) { setState("error"); return; }
    (async () => {
      try {
        const { data } = await api.post("/shop/orders/verify", { order_id: orderId });
        if (data.paid) { setOrder(data.order); setState("paid"); clear(); }
        else setState("unpaid");
      } catch { setState("error"); }
    })();
  }, [orderId, clear]);

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="order-return">
        {state === "verifying" && (
          <>
            <div className="h-12 w-12 mx-auto rounded-full border-2 border-slate-200 border-t-purple-700 animate-spin" />
            <h1 className="mt-6 text-2xl text-slate-900" style={SERIF}>Confirming your payment…</h1>
            <p className="mt-2 text-slate-600">One moment while we verify your order with Square.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto" style={{ color: C.green }} />
            <h1 className="mt-6 text-3xl text-slate-900" style={SERIF} data-testid="order-paid">Thank you for your order!</h1>
            <p className="mt-2 text-slate-600">Order <span className="font-semibold">{order?.order_number}</span> is confirmed. A receipt has been sent to your email.</p>
            <div className="mt-6 rounded-2xl bg-white p-5 text-left" style={{ border: `1px solid ${C.line}` }}>
              {(order?.items || []).map((it) => (
                <div key={it.product_id} className="flex justify-between text-sm py-1">
                  <span className="text-slate-700">{it.name} × {it.quantity}</span>
                  <span className="text-slate-900 font-medium">${Number(it.line_total).toFixed(2)}</span>
                </div>
              ))}
              <div className="mt-3 pt-3 flex justify-between font-semibold" style={{ borderTop: `1px solid ${C.line}` }}>
                <span>Total</span><span>${Number(order?.total || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="mt-7 flex justify-center gap-3">
              <PrimaryBtn to={ROUTES.shop}>Continue Shopping</PrimaryBtn>
              <GhostBtn to={ROUTES.home}>Back Home</GhostBtn>
            </div>
          </>
        )}
        {(state === "unpaid" || state === "error") && (
          <>
            <AlertCircle className="h-14 w-14 mx-auto text-amber-500" />
            <h1 className="mt-6 text-2xl text-slate-900" style={SERIF} data-testid="order-unpaid">We couldn't confirm your payment yet</h1>
            <p className="mt-2 text-slate-600">If you were charged, please contact the clinic and we'll sort it out right away. Your cart has been kept.</p>
            <div className="mt-7 flex justify-center gap-3">
              <PrimaryBtn to={ROUTES.cart}>Return to Cart</PrimaryBtn>
              <GhostBtn to={ROUTES.shop}>Back to Shop</GhostBtn>
            </div>
          </>
        )}
      </div>
    </PublicLayout>
  );
}
